import { z } from 'zod';
import { isoDateSchema, isoDateTimeSchema, reasonSchema, uuidSchema, versionSchema, } from '../common/primitives.js';
export const OBLIGATION_CATEGORIES = [
    'PRODUCT',
    'SALES',
    'FINANCE',
    'FUNDRAISING',
    'PARTNERSHIPS',
    'ENGINEERING',
    'OPERATIONS',
    'LEGAL_ADMIN',
    'CUSTOMER_SUCCESS',
    'OTHER',
];
export const obligationCategorySchema = z.enum(OBLIGATION_CATEGORIES);
export const OBLIGATION_CADENCES = ['DAILY', 'WEEKLY', 'MONTHLY', 'QUARTERLY', 'ONE_TIME'];
export const obligationCadenceSchema = z.enum(OBLIGATION_CADENCES);
export const EVIDENCE_REQUIREMENTS = ['NONE', 'OPTIONAL', 'REQUIRED'];
export const evidenceRequirementSchema = z.enum(EVIDENCE_REQUIREMENTS);
export const OBLIGATION_STATUSES = ['ACTIVE', 'PAUSED', 'RETIRED'];
export const obligationStatusSchema = z.enum(OBLIGATION_STATUSES);
/** Local wall-clock time `HH:MM` (24h) in the workspace time zone. */
export const localTimeSchema = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use a 24-hour time such as 18:00');
/**
 * Structured due rule (spec §7: "day/time or period end"). Times are interpreted in the workspace
 * time zone; the kind must match the obligation cadence.
 */
export const dueRuleSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('DAILY'), time: localTimeSchema }),
    z.object({
        kind: z.literal('WEEKLY'),
        /** ISO weekday: 1 = Monday … 7 = Sunday. */
        weekday: z.number().int().min(1).max(7),
        time: localTimeSchema,
    }),
    z.object({
        kind: z.literal('MONTHLY'),
        /** Day of month 1–28, or `LAST` for the last day of the month. */
        day: z.union([z.number().int().min(1).max(28), z.literal('LAST')]),
        time: localTimeSchema,
    }),
    /** Due on the last day of each calendar quarter. */
    z.object({ kind: z.literal('QUARTERLY'), time: localTimeSchema }),
    z.object({ kind: z.literal('ONE_TIME'), date: isoDateSchema, time: localTimeSchema }),
]);
export const obligationSchema = z.object({
    id: uuidSchema,
    founderProfileId: uuidSchema,
    category: obligationCategorySchema,
    commitment: z.string(),
    cadence: obligationCadenceSchema,
    evidenceRequirement: evidenceRequirementSchema,
    evidenceDescription: z.string().nullable(),
    dueRule: dueRuleSchema,
    gracePeriodHours: z.number().int().nullable(),
    weight: z.number().int().nullable(),
    status: obligationStatusSchema,
    startsOn: isoDateSchema,
    endsOn: isoDateSchema.nullable(),
    decisionId: uuidSchema.nullable(),
    assignedBy: z.string(),
    /** Next due instant computed in the workspace time zone; `null` when paused/retired/ended. */
    nextDueAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    version: versionSchema,
});
export const obligationListSchema = z.object({
    timezone: z.string(),
    items: z.array(obligationSchema),
});
const obligationFieldsSchema = z.object({
    category: obligationCategorySchema,
    commitment: z.string().trim().min(3).max(2000),
    cadence: obligationCadenceSchema,
    evidenceRequirement: evidenceRequirementSchema,
    evidenceDescription: z.string().trim().max(1000).nullable().optional(),
    dueRule: dueRuleSchema,
    gracePeriodHours: z.number().int().min(0).max(720).nullable().optional(),
    /** Dashboard visibility only (spec §7): never used for binding employment/equity decisions. */
    weight: z.number().int().min(1).max(10).nullable().optional(),
    startsOn: isoDateSchema,
    endsOn: isoDateSchema.nullable().optional(),
    decisionId: uuidSchema.nullable().optional(),
});
function cadenceMatchesRule(value) {
    return value.cadence === undefined || value.dueRule === undefined
        ? true
        : value.cadence === value.dueRule.kind;
}
function endsAfterStart(value) {
    return (value.startsOn === undefined ||
        value.endsOn === undefined ||
        value.endsOn === null ||
        value.endsOn >= value.startsOn);
}
export const createObligationRequestSchema = obligationFieldsSchema
    .refine(cadenceMatchesRule, { message: 'Due rule must match the cadence', path: ['dueRule'] })
    .refine(endsAfterStart, { message: 'End date must not be before start', path: ['endsOn'] });
export const updateObligationRequestSchema = obligationFieldsSchema
    .omit({ startsOn: true, decisionId: true })
    .partial()
    .extend({ version: versionSchema })
    .refine((value) => (value.cadence === undefined) === (value.dueRule === undefined), {
    message: 'Cadence and due rule must be changed together',
    path: ['dueRule'],
})
    .refine(cadenceMatchesRule, { message: 'Due rule must match the cadence', path: ['dueRule'] });
export const changeObligationStatusRequestSchema = z.object({
    version: versionSchema,
    status: obligationStatusSchema,
    reason: reasonSchema,
});
export const OBLIGATION_PERIOD_STATUSES = [
    'NOT_STARTED',
    'ON_TRACK',
    'AT_RISK',
    'COMPLETED',
    'MISSED',
    'EXCUSED',
];
export const obligationPeriodStatusSchema = z.enum(OBLIGATION_PERIOD_STATUSES);
/** Final period outcomes: no further status changes (history stays in the events). */
export const FINAL_OBLIGATION_PERIOD_STATUSES = [
    'COMPLETED',
    'MISSED',
    'EXCUSED',
];
export const obligationPeriodEventSchema = z.object({
    fromStatus: obligationPeriodStatusSchema.nullable(),
    toStatus: obligationPeriodStatusSchema,
    comment: z.string().nullable(),
    evidenceUrl: z.string().nullable(),
    taskId: uuidSchema.nullable(),
    /** `null` for system transitions (period passed without completion). */
    actor: z.string().nullable(),
    occurredAt: isoDateTimeSchema,
});
/**
 * One period of a standing obligation (cut from its due rule in the workspace time zone). Missed
 * periods are facts for transparency; consequences remain a human governance decision (spec §7.13).
 */
export const obligationPeriodSchema = z.object({
    id: uuidSchema,
    obligationId: uuidSchema,
    founderProfileId: uuidSchema,
    commitment: z.string(),
    category: obligationCategorySchema,
    evidenceRequirement: evidenceRequirementSchema,
    periodStart: isoDateSchema,
    periodEnd: isoDateSchema,
    dueAt: isoDateTimeSchema,
    /** Due time plus the obligation's grace period: after this an unfinished period is missed. */
    graceEndsAt: isoDateTimeSchema,
    status: obligationPeriodStatusSchema,
    events: z.array(obligationPeriodEventSchema),
    /** Weekly updates that reported against this obligation during the period. */
    reportedInUpdates: z.number().int(),
    canUpdate: z.boolean(),
    canExcuse: z.boolean(),
    version: versionSchema,
});
export const obligationPeriodListSchema = z.object({
    timezone: z.string(),
    items: z.array(obligationPeriodSchema),
});
export const obligationPeriodQuerySchema = z.object({
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
});
export const updateObligationPeriodRequestSchema = z
    .object({
    version: versionSchema,
    status: z.enum(['ON_TRACK', 'AT_RISK', 'COMPLETED', 'EXCUSED']),
    comment: z.string().trim().max(2000).nullable().optional(),
    evidenceUrl: z
        .string()
        .trim()
        .max(2048)
        .pipe(z.url({ protocol: /^https?$/, message: 'Enter an http(s) link' }))
        .nullable()
        .optional(),
    taskId: uuidSchema.nullable().optional(),
})
    .refine((value) => value.status !== 'EXCUSED' ||
    (value.comment !== undefined && value.comment !== null && value.comment.length >= 3), { message: 'Explain why this period is excused', path: ['comment'] });
//# sourceMappingURL=obligations.js.map